// src/features/bom/services/bomApi.ts

import apiClient from '@/lib/api-client';

export interface BomRecipeItem {
    id: string;
    recipeId: string;
    rawMaterialId: string;
    rawMaterialName?: string;
    rawMaterialSku?: string;
    quantity: string;
    uomId: string;
    uomName?: string;
    uomAbbreviation?: string;
    wasteFactor: string;
    sortOrder: string;
    notes: string | null;
    cost?: number;               // computed cost for this ingredient
    baseQuantity?: number;       // computed base quantity
    conversionRate?: string | null; // custom conversion rate if provided
    customUomName?: string;      // optional custom unit name
}

export interface BomRecipe {
    id: string;
    tenantId: string;
    name: string;
    description: string | null;
    finishedProductId: string;
    finishedProductName?: string;
    yieldQuantity: string;
    yieldUomId: string;
    yieldUomName?: string;
    preparationInstructions: string | null;
    preparationTime: string | null;
    isActive: boolean;
    items: BomRecipeItem[];
    createdAt: string;
    updatedAt: string;
    totalCost?: number;          // total ingredient cost for one yield
    costPerYield?: number;       // cost per single serving
    // Optional product-related fields sometimes returned in enriched data
    finishedProductRetailPrice?: string;
    finishedProductCategoryId?: string;
}

export interface BomCategory {
    id: string;
    name: string;
    icon: string;
    color: string;
    sortOrder: number;
}
export interface ProductionPreviewItem {
    rawMaterialId: string;
    rawMaterialName: string;
    requiredBaseQty: number;
    availableBaseQty: number;
    remainingAfter: number;
    insufficient: boolean;
    cost: number;
    uomName: string;
}

export interface ProductionPreview {
    items: ProductionPreviewItem[];
    totalCost: number;
}

export async function getAllRecipes(tenantId: string): Promise<BomRecipe[]> {
    const { data } = await apiClient.get(`/tenants/${tenantId}/bom/recipes`);
    return data?.data || [];
}

export async function getRecipe(tenantId: string, recipeId: string): Promise<BomRecipe> {
    const { data } = await apiClient.get(`/tenants/${tenantId}/bom/recipes/${recipeId}`);
    return data?.data;
}

export async function getRecipesByProduct(tenantId: string, productId: string): Promise<BomRecipe[]> {
    const { data } = await apiClient.get(`/tenants/${tenantId}/bom/products/${productId}/recipes`);
    return data?.data || [];
}

export async function createRecipe(tenantId: string, payload: any): Promise<BomRecipe> {
    const { data } = await apiClient.post(`/tenants/${tenantId}/bom/recipes`, payload);
    return data?.data;
}

export async function updateRecipe(tenantId: string, recipeId: string, payload: any): Promise<BomRecipe> {
    const { data } = await apiClient.patch(`/tenants/${tenantId}/bom/recipes/${recipeId}`, payload);
    return data?.data;
}

export async function deactivateRecipe(tenantId: string, recipeId: string): Promise<void> {
    await apiClient.post(`/tenants/${tenantId}/bom/recipes/${recipeId}/deactivate`);
}

export async function explodeBom(tenantId: string, productId: string, quantity: number = 1): Promise<any[]> {
    const { data } = await apiClient.get(
        `/tenants/${tenantId}/bom/products/${productId}/explode`,
        { params: { quantity: String(quantity) } }
    );
    return data?.data || [];
}

export async function startProduction(tenantId: string, data: { recipeId: string; quantityProduced: number; notes?: string; branchId?: string }) {
    const payload: any = {
        recipeId: data.recipeId,
        quantityProduced: data.quantityProduced,
    };
    if (data.notes) payload.notes = data.notes;
    if (data.branchId && data.branchId.trim()) payload.branchId = data.branchId.trim();

    const { data: response } = await apiClient.post(`/tenants/${tenantId}/bom/production/start`, payload);
    return response?.data;
}

// export async function getTodayBatches(tenantId: string) {
//     const { data } = await apiClient.get(`/tenants/${tenantId}/bom/production/today`);
//     return data?.data || [];
// }


export async function getTodayBatches(tenantId: string, branchId?: string) {
    const params: Record<string, string> = {};
    if (branchId) params.branchId = branchId;

    const { data } = await apiClient.get(`/tenants/${tenantId}/bom/production/today`, { params });
    return data?.data || [];
}

export async function getBatchDetail(tenantId: string, batchId: string) {
    const { data } = await apiClient.get(`/tenants/${tenantId}/bom/production/${batchId}`);
    return data?.data;
}

export async function completeBatch(tenantId: string, batchId: string, wastedPortions: number = 0) {
    const { data } = await apiClient.post(`/tenants/${tenantId}/bom/production/${batchId}/complete`, { wastedPortions });
    return data?.data;
}

export async function getBatchHistory(tenantId: string) {
    const { data } = await apiClient.get(`/tenants/${tenantId}/bom/production/history`);
    return data?.data || [];
}

export async function previewProduction(
    tenantId: string,
    recipeId: string,
    quantityProduced: number,
    branchId?: string
): Promise<ProductionPreview> {
    const { data } = await apiClient.post(
        `/tenants/${tenantId}/bom/production/preview`,
        { recipeId, quantityProduced, branchId }
    );
    return data?.data;
}

export async function addPortions(
    tenantId: string,
    batchId: string,
    quantityToAdd: number,
    branchId?: string
): Promise<any> {
    const payload: { quantityToAdd: number; branchId?: string } = { quantityToAdd };
    if (branchId && branchId.trim()) {
        payload.branchId = branchId.trim();
    }
    const { data } = await apiClient.post(
        `/tenants/${tenantId}/bom/production/${batchId}/add-portions`,
        payload
    );
    return data?.data;
}

export async function decrementPortions(
    tenantId: string,
    items: Array<{ recipeId: string; quantity: number }>
): Promise<void> {
    await apiClient.post(`/tenants/${tenantId}/bom/production/decrement-portions`, { items });
}

// ============================================================================
// ⏱️ TIMED WRAPPERS FOR POS
// ============================================================================

const DEFAULT_TIMEOUT = 30000; // 30 seconds

export async function parkCartWithTimeout(
    tenantId: string,
    payload: any,
    timeout: number = DEFAULT_TIMEOUT
): Promise<any> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
        const { data } = await apiClient.post(`/tenants/${tenantId}/sales/cart`, payload, {
            signal: controller.signal,
        });
        return data?.data || data;
    } finally {
        clearTimeout(timeoutId);
    }
}

export async function checkoutWithTimeout(
    tenantId: string,
    cartId: string,
    payments: any[],
    discountTotal: string,
    timeout: number = DEFAULT_TIMEOUT
): Promise<any> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
        const formattedPayments = payments.map((p) => ({
            paymentMethod: p.paymentMethod,
            amount: p.amount?.toString() || '0',
            reference: (p.reference && String(p.reference).trim()) ? String(p.reference).trim() : undefined,
            bankAccountId: (p.bankAccountId && String(p.bankAccountId).trim()) ? String(p.bankAccountId).trim() : undefined,
            mobileMoneyProvider: (p.mobileMoneyProvider && String(p.mobileMoneyProvider).trim()) ? String(p.mobileMoneyProvider).trim() : undefined,
        }));

        const { data } = await apiClient.post(
            `/tenants/${tenantId}/sales/checkout`,
            { cartId, payments: formattedPayments, discountTotal: discountTotal || '0' },
            { signal: controller.signal }
        );
        return data?.data || data;
    } finally {
        clearTimeout(timeoutId);
    }
}

export async function decrementPortionsWithTimeout(
    tenantId: string,
    items: Array<{ recipeId: string; quantity: number }>
): Promise<void> {
    // Use a shorter timeout for this background operation
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 seconds

    try {
        await apiClient.post(
            `/tenants/${tenantId}/bom/production/decrement-portions`,
            { items },
            { signal: controller.signal }
        );
    } finally {
        clearTimeout(timeoutId);
    }
}

export async function directCheckoutWithTimeout(
    tenantId: string,
    saleData: {
        branchId: string;
        shiftId?: string | null;
        items: Array<{
            productId: string;
            variantId?: string;
            uomId?: string;
            quantity: string;
            unitPrice: string;
            discountAmount: string;
            notes?: string;
        }>;
        payments: any[];
        customerId?: string | null;
        saleType?: string;
        tableId?: string | null;
        posTerminalId?: string | null;
        notes?: string;
        discountTotal?: string;
        discountReason?: string;
        taxExempt?: boolean;
    },
    timeout: number = DEFAULT_TIMEOUT
): Promise<any> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
        const formattedPayments = saleData.payments.map((p) => {
            let method = p.paymentMethod;
            if (method === 'card') method = 'pos_card';
            return {
                paymentMethod: method,
                amount: p.amount?.toString() || '0',
                reference: (p.reference && String(p.reference).trim()) ? String(p.reference).trim() : undefined,
                bankAccountId: (p.bankAccountId && String(p.bankAccountId).trim()) ? String(p.bankAccountId).trim() : undefined,
                mobileMoneyProvider: (p.mobileMoneyProvider && String(p.mobileMoneyProvider).trim()) ? String(p.mobileMoneyProvider).trim() : undefined,
            };
        });

        const payload = {
            ...saleData,
            payments: formattedPayments,
            discountTotal: saleData.discountTotal || '0',
        };

        const { data } = await apiClient.post(
            `/tenants/${tenantId}/sales/direct`,
            payload,
            { signal: controller.signal }
        );
        return data?.data || data;
    } finally {
        clearTimeout(timeoutId);
    }
}

